import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { extractLogsService } from '../services/api';
import { ErrorState, ErrorBanner } from './ErrorMessage';
import { getErrorMessage } from '../utils/errors';
import { formatDateTime } from '../utils/format';
import StatusDonutChart from './charts/StatusDonutChart';
import DurationLineChart from './charts/DurationLineChart';

function RunsList() {
    const [runs, setRuns] = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [facilitiesError, setFacilitiesError] = useState(null);
    const [days, setDays] = useState(7);
    const [selectedFacility, setSelectedFacility] = useState('');
    const [alertExpanded, setAlertExpanded] = useState(true);

    useEffect(() => {
        loadFacilities();
    }, []);

    useEffect(() => {
        loadRuns();
    }, [days, selectedFacility]);

    const loadFacilities = async () => {
        try {
            const data = await extractLogsService.getFacilities();
            setFacilities(data);
            setFacilitiesError(null);
        } catch (err) {
            setFacilitiesError(`Couldn't load the facility list: ${getErrorMessage(err)}`);
        }
    };

    const loadRuns = async () => {
        try {
            setLoading(true);
            const data = await extractLogsService.getRuns(days, selectedFacility || null);
            setRuns(data);
            setError(null);
        } catch (err) {
            setError(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const getStatusClass = (status) => {
        switch(status) {
            case 'Complete': return 'complete';
            case 'Running': return 'running';
            case 'Failed': return 'failed';
            case 'Skipped': return 'skipped';
            default: return '';
        }
    };

    // Calculate stats
    const stats = {
        total: runs.length,
        complete: runs.filter(r => r.status === 'Complete').length,
        running: runs.filter(r => r.status === 'Running').length,
        failed: runs.filter(r => r.status === 'Failed').length,
        skipped: runs.filter(r => r.status === 'Skipped').length
    };

    // Find facilities where maintenance script failed and pulls were skipped
    const maintenanceFailures = runs
        .filter(r => r.status === 'Skipped' && r.statusMessage && r.statusMessage.toLowerCase().includes('failed'))
        .reduce((acc, run) => {
            if (!acc.find(f => f.facilityID === run.facilityID)) {
                acc.push({ facilityID: run.facilityID, statusMessage: run.statusMessage });
            }
            return acc;
        }, []);

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <div className="loading-text">Loading extract runs...</div>
            </div>
        );
    }

    if (error) {
        return <ErrorState title="Couldn't load extract runs" message={error} onRetry={loadRuns} />;
    }

    return (
        <div className="runs-list">
            <ErrorBanner
                message={facilitiesError}
                onRetry={loadFacilities}
                onDismiss={() => setFacilitiesError(null)}
            />

            <div className="page-header">
                <h1>Extract Runs</h1>
            </div>

            {maintenanceFailures.length > 0 && (
                <div className={`alert alert-error maintenance-alert ${!alertExpanded ? 'collapsed' : ''}`}>
                    <div 
                        className="maintenance-alert-header" 
                        onClick={() => setAlertExpanded(!alertExpanded)}
                        style={{ cursor: 'pointer' }}
                    >
                        <span className="alert-icon">⚠️</span>
                        <strong>Nightly Maintenance Script Failed</strong>
                        <span className="alert-count">{maintenanceFailures.length} facility{maintenanceFailures.length > 1 ? 'ies' : ''}</span>
                        <span className={`collapse-icon ${alertExpanded ? 'expanded' : ''}`}>▼</span>
                    </div>
                    {alertExpanded && (
                        <div className="maintenance-alert-body">
                            Data pulls were skipped for the following facilities due to maintenance script failures:
                            <ul className="facility-failure-list">
                                {maintenanceFailures.map(f => (
                                    <li key={f.facilityID}>
                                        <span className="facility-id">{f.facilityID}</span>
                                        {f.statusMessage && (
                                            <span className="failure-detail">{f.statusMessage}</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            <div className="card">
                <div className="filters">
                    <div className="filter-group">
                        <label>Time Period</label>
                        <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
                            <option value={1}>Last 24 hours</option>
                            <option value={7}>Last 7 days</option>
                            <option value={14}>Last 14 days</option>
                            <option value={30}>Last 30 days</option>
                        </select>
                    </div>
                    
                    <div className="filter-group">
                        <label>Facility</label>
                        <select value={selectedFacility} onChange={(e) => setSelectedFacility(e.target.value)}>
                            <option value="">All Facilities</option>
                            {facilities.map(f => (
                                <option key={f} value={f}>{f}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="stats-grid">
                <div className="stat-card info">
                    <div className="stat-value">{stats.total}</div>
                    <div className="stat-label">Total Runs</div>
                </div>
                <div className="stat-card success">
                    <div className="stat-value">{stats.complete}</div>
                    <div className="stat-label">Completed</div>
                </div>
                <div className="stat-card warning">
                    <div className="stat-value">{stats.running}</div>
                    <div className="stat-label">Running</div>
                </div>
                <div className="stat-card error">
                    <div className="stat-value">{stats.failed}</div>
                    <div className="stat-label">Failed</div>
                </div>
            </div>

            {/* Dashboard Charts */}
            <div className="dashboard-charts">
                <div className="chart-card compact">
                    <h3 className="chart-title">Status Overview</h3>
                    <StatusDonutChart runs={runs} compact />
                </div>
                <div className="chart-card compact wide">
                    <h3 className="chart-title">Duration Trend</h3>
                    <DurationLineChart runs={runs} compact />
                </div>
            </div>

            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th>Run ID</th>
                            <th>Facility</th>
                            <th>Start Time</th>
                            <th>End Time</th>
                            <th className="text-right">Duration</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {runs.map(run => (
                            <tr key={run.runID}>
                                <td>#{run.runID}</td>
                                <td className="facility">{run.facilityID}</td>
                                <td>{formatDateTime(run.startTime)}</td>
                                <td>{formatDateTime(run.endTime)}</td>
                                <td className="text-right">{run.totalMinutes?.toFixed(2) || '-'} min</td>
                                <td>
                                    <span className={`status-badge ${run.isHistorical ? 'info' : ''}`}>
                                        {run.isHistorical ? 'Historical' : 'Daily'}
                                    </span>
                                </td>
                                <td>
                                    <span className={`status-badge ${getStatusClass(run.status)}`}>
                                        {run.status}
                                    </span>
                                </td>
                                <td>
                                    <Link to={`/runs/${run.runID}`} className="btn btn-primary">
                                        View Details
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            
            {runs.length === 0 && (
                <div className="no-data">No runs found for the selected criteria.</div>
            )}
        </div>
    );
}

export default RunsList;